// Activity events emitted by `identity/auth`.
//
// Kind strings use the platform-wide convention of dot-segments separated
// by hyphens (`user.password-reset-requested`). The `USER_ACTIVITY_KINDS`
// constant below is the source-of-truth for these kind strings — every
// publishActivity call site MUST reference one of them, never a string
// literal, so that schema evolution (renames, new kinds) is mechanically
// auditable from a single import site.
//
// Per-kind payload schemas live alongside, all version 1. Subscribers may
// pin to one or more versions via ActivitySubscriber.versions.
import { z } from 'zod';
import { AuthDeviceSummary } from './device.js';
// ---------------------------------------------------------------------------
// Kind constants — every auth-emitted Activity references these.
// ---------------------------------------------------------------------------
export const USER_ACTIVITY_KINDS = {
    Registered: 'user.registered',
    LoggedIn: 'user.logged-in',
    LoggedOut: 'user.logged-out',
    AdminCreated: 'user.admin-created',
    ParentCreatedChild: 'user.parent-created-child',
    PasswordResetRequested: 'user.password-reset-requested',
    PasswordResetCompleted: 'user.password-reset-completed',
    ChildPasswordResetRequested: 'user.child-password-reset-requested',
    ChildPasswordResetCompleted: 'user.child-password-reset-completed',
    EmailVerificationRequested: 'user.email-verification-requested',
    EmailVerified: 'user.email-verified',
    PhoneVerificationRequested: 'user.phone-verification-requested',
    PhoneVerified: 'user.phone-verified',
    UsernameChanged: 'user.username-changed',
};
/** Glob pattern matching every auth-emitted kind. Used by subscribers that
 *  want a coarse "tell me about anything user-shaped" subscription (e.g. the
 *  notifications dispatcher). */
export const USER_ACTIVITY_KIND_GLOB = 'user.*';
// ---------------------------------------------------------------------------
// Per-kind payload schemas. Every payload is wrapped by ActivityEvent at the
// transport layer; these schemas describe ONLY the `payload` field.
// ---------------------------------------------------------------------------
export const UserRegisteredEvent = z.object({
    type: z.literal('user.registered'),
    userId: z.string().uuid(),
    /** 'email' | 'phone' — which channel the registration used. */
    via: z.enum(['email', 'phone']),
    /** Masked address (e.g. `a***e@example.com` / `+86***5678`). Never raw. */
    identifier: z.string().min(1),
    sessionId: z.string().uuid().optional(),
    device: AuthDeviceSummary.optional(),
    occurredAt: z.string().datetime({ offset: true }),
});
export const UserLoggedInEvent = z.object({
    type: z.literal('user.logged-in'),
    userId: z.string().uuid(),
    /** Identifier kind used to log in (the SAME user may have multiple). */
    via: z.enum(['email', 'phone', 'username']),
    sessionId: z.string().uuid().optional(),
    device: AuthDeviceSummary.optional(),
    occurredAt: z.string().datetime({ offset: true }),
});
export const UserLoggedOutEvent = z.object({
    type: z.literal('user.logged-out'),
    userId: z.string().uuid(),
    sessionId: z.string().uuid().optional(),
    device: AuthDeviceSummary.optional(),
    occurredAt: z.string().datetime({ offset: true }),
});
export const UserAdminCreatedEvent = z.object({
    type: z.literal('user.admin-created'),
    userId: z.string().uuid(),
    /** Admin who provisioned this account. */
    createdBy: z.string().uuid(),
    /** Initial role assigned by the admin. */
    role: z.string().min(1),
    occurredAt: z.string().datetime({ offset: true }),
});
export const ParentCreatedChildEvent = z.object({
    type: z.literal('user.parent-created-child'),
    parentId: z.string().uuid(),
    childId: z.string().uuid(),
    occurredAt: z.string().datetime({ offset: true }),
});
export const PasswordResetRequestedEvent = z.object({
    type: z.literal('user.password-reset-requested'),
    userId: z.string().uuid(),
    /** Reset channel. Legacy email events may omit this and are treated as email. */
    via: z.enum(['email', 'phone']).optional(),
    /** Server-side opaque email-token id (NOT the raw reset token). */
    tokenId: z.string().uuid().optional(),
    /** Server-side opaque SMS-code id (NOT the raw 6-digit code). */
    codeId: z.string().uuid().optional(),
    occurredAt: z.string().datetime({ offset: true }),
});
export const PasswordResetCompletedEvent = z.object({
    type: z.literal('user.password-reset-completed'),
    userId: z.string().uuid(),
    occurredAt: z.string().datetime({ offset: true }),
});
export const ChildPasswordResetRequestedEvent = z.object({
    type: z.literal('user.child-password-reset-requested'),
    requestId: z.string().uuid(),
    childId: z.string().uuid(),
    childDisplayName: z.string().min(1),
    parentIds: z.array(z.string().uuid()).min(1),
    occurredAt: z.string().datetime({ offset: true }),
});
export const ChildPasswordResetCompletedEvent = z.object({
    type: z.literal('user.child-password-reset-completed'),
    requestId: z.string().uuid(),
    parentId: z.string().uuid(),
    childId: z.string().uuid(),
    childDisplayName: z.string().min(1),
    occurredAt: z.string().datetime({ offset: true }),
});
export const EmailVerificationRequestedEvent = z.object({
    type: z.literal('user.email-verification-requested'),
    userId: z.string().uuid(),
    /** Server-side token id (NOT the raw token). */
    tokenId: z.string().uuid(),
    occurredAt: z.string().datetime({ offset: true }),
});
export const EmailVerifiedEvent = z.object({
    type: z.literal('user.email-verified'),
    userId: z.string().uuid(),
    occurredAt: z.string().datetime({ offset: true }),
});
export const PhoneVerificationRequestedEvent = z.object({
    type: z.literal('user.phone-verification-requested'),
    userId: z.string().uuid(),
    /** Server-side code id (NOT the raw 6-digit code). */
    codeId: z.string().uuid(),
    occurredAt: z.string().datetime({ offset: true }),
});
export const PhoneVerifiedEvent = z.object({
    type: z.literal('user.phone-verified'),
    userId: z.string().uuid(),
    occurredAt: z.string().datetime({ offset: true }),
});
export const UsernameChangedEvent = z.object({
    type: z.literal('user.username-changed'),
    userId: z.string().uuid(),
    /** Previous username — null when this is the first time a username is set. */
    fromUsername: z.string().nullable(),
    toUsername: z.string().min(1),
    occurredAt: z.string().datetime({ offset: true }),
});
//# sourceMappingURL=events.js.map